/**
 * Alerts Service
 * 
 * Handles alert-related API operations.
 * Connects to func-axn-analyst-api via SWA linked backend.
 * 
 * API endpoints:
 * - GET /api/incidents/:tenantId/:incidentId/alerts
 * - GET /api/alerts/:tenantId/:alertId
 */

import type { Alert, AlertDetail, AlertEvidence } from '../data/types';
import { apiFetch, type ApiResponse } from './api';

// ============================================================================
// API Response Types
// ============================================================================

interface AlertsResponse {
  alerts: Array<{
    alertId: string;
    tenantId: string;
    incidentId: string;
    title: string;
    description: string | null;
    severity: string;
    category: string | null;
    status: string | null;
    classification: string | null;
    determination: string | null;
    serviceSource: string | null;
    detectionSource: string | null;
    firstActivityDateTime: string | null;
    lastActivityDateTime: string | null;
    createdDateTime: string;
    resolvedDateTime: string | null;
    recommendedActions: string[] | null;
    mitreTechniques: string[] | null;
    threatDisplayName: string | null;
    threatFamilyName: string | null;
    alertWebUrl: string | null;
    customDetails: Record<string, string[]> | null;
    evidenceCount?: number;
  }>;
}

interface AlertDetailResponse {
  alert: AlertsResponse['alerts'][0];
  evidence: Array<{
    evidenceId: string;
    evidenceType: string;
    evidenceRole: string | null;
    displayName: string | null;
    verdict: string | null;
    remediationStatus: string | null;
    processId: number | null;
    parentProcessId: number | null;
    processCommandLine: string | null;
    processCreationTime: string | null;
    imageFile: string | null;
    deviceDnsName: string | null;
    mdeDeviceId: string | null;
    osPlatform: string | null;
    osVersion: string | null;
    userPrincipalName: string | null;
    accountName: string | null;
    domainName: string | null;
    fileName: string | null;
    filePath: string | null;
    sha256: string | null;
    sha1: string | null;
    fileSize: number | null;
    ipAddress: string | null;
    url: string | null;
    detailedData: Record<string, unknown> | null;
  }>;
}

// ============================================================================
// GET ALERTS
// ============================================================================

/**
 * Fetches all alerts for an incident
 */
export async function getIncidentAlerts(
  tenantId: string,
  incidentId: string
): Promise<ApiResponse<Alert[]>> {
  const result = await apiFetch<AlertsResponse>(
    `/incidents/${encodeURIComponent(tenantId)}/${encodeURIComponent(incidentId)}/alerts`
  );

  if (!result.success) {
    return { data: [], success: false, error: result.error };
  }

  const alerts: Alert[] = result.data.alerts.map(a => ({
    alertId: a.alertId,
    tenantId: a.tenantId,
    incidentId: a.incidentId,
    title: a.title,
    description: a.description,
    severity: (a.severity?.toLowerCase() || 'medium') as Alert['severity'],
    category: a.category,
    status: a.status,
    classification: a.classification,
    determination: a.determination,
    serviceSource: a.serviceSource,
    detectionSource: a.detectionSource,
    firstActivityDateTime: a.firstActivityDateTime,
    lastActivityDateTime: a.lastActivityDateTime,
    createdDateTime: a.createdDateTime,
    resolvedDateTime: a.resolvedDateTime,
    recommendedActions: a.recommendedActions,
    mitreTechniques: a.mitreTechniques,
    threatDisplayName: a.threatDisplayName,
    threatFamilyName: a.threatFamilyName,
    alertWebUrl: a.alertWebUrl,
    customDetails: a.customDetails ?? null,
    evidenceCount: a.evidenceCount,
  }));

  return { data: alerts, success: true };
}

/**
 * Fetches detailed information for a single alert
 */
export async function getAlertDetail(
  tenantId: string,
  alertId: string
): Promise<ApiResponse<AlertDetail | null>> {
  const result = await apiFetch<AlertDetailResponse>(
    `/alerts/${encodeURIComponent(tenantId)}/${encodeURIComponent(alertId)}`
  );

  if (!result.success) {
    if (result.error?.includes('404')) {
      return { data: null, success: true };
    }
    return { data: null, success: false, error: result.error };
  }

  const alert: Alert = {
    alertId: result.data.alert.alertId,
    tenantId: result.data.alert.tenantId,
    incidentId: result.data.alert.incidentId,
    title: result.data.alert.title,
    description: result.data.alert.description,
    severity: (result.data.alert.severity?.toLowerCase() || 'medium') as Alert['severity'],
    category: result.data.alert.category,
    status: result.data.alert.status,
    classification: result.data.alert.classification,
    determination: result.data.alert.determination,
    serviceSource: result.data.alert.serviceSource,
    detectionSource: result.data.alert.detectionSource,
    firstActivityDateTime: result.data.alert.firstActivityDateTime,
    lastActivityDateTime: result.data.alert.lastActivityDateTime,
    createdDateTime: result.data.alert.createdDateTime,
    resolvedDateTime: result.data.alert.resolvedDateTime,
    recommendedActions: result.data.alert.recommendedActions,
    mitreTechniques: result.data.alert.mitreTechniques,
    threatDisplayName: result.data.alert.threatDisplayName,
    threatFamilyName: result.data.alert.threatFamilyName,
    alertWebUrl: result.data.alert.alertWebUrl,
    customDetails: result.data.alert.customDetails ?? null,
  };

  const evidence: AlertEvidence[] = result.data.evidence.map(e => ({
    evidenceId: e.evidenceId,
    evidenceType: e.evidenceType,
    evidenceRole: e.evidenceRole,
    displayName: e.displayName,
    verdict: e.verdict,
    remediationStatus: e.remediationStatus,
    processId: e.processId,
    parentProcessId: e.parentProcessId,
    processCommandLine: e.processCommandLine,
    processCreationTime: e.processCreationTime,
    imageFile: e.imageFile,
    deviceDnsName: e.deviceDnsName,
    mdeDeviceId: e.mdeDeviceId,
    osPlatform: e.osPlatform,
    osVersion: e.osVersion,
    userPrincipalName: e.userPrincipalName,
    accountName: e.accountName,
    domainName: e.domainName,
    fileName: e.fileName,
    filePath: e.filePath,
    sha256: e.sha256,
    sha1: e.sha1,
    fileSize: e.fileSize,
    ipAddress: e.ipAddress,
    url: e.url,
    detailedData: e.detailedData,
  }));

  return { data: { alert, evidence }, success: true };
}

/**
 * Gets process evidence sorted for tree building
 */
export function getProcessEvidence(evidence: AlertEvidence[]): AlertEvidence[] {
  return evidence
    .filter(e => e.evidenceType === 'process')
    .sort((a, b) => {
      // Sort by creation time, earliest first
      if (a.processCreationTime && b.processCreationTime) {
        return new Date(a.processCreationTime).getTime() - new Date(b.processCreationTime).getTime();
      }
      return 0;
    });
}

/**
 * Builds a process tree structure from evidence
 */
export interface ProcessTreeNode {
  evidence: AlertEvidence;
  children: ProcessTreeNode[];
}

export function buildProcessTree(evidence: AlertEvidence[]): ProcessTreeNode[] {
  const processEvidence = getProcessEvidence(evidence);
  const processMap = new Map<number, ProcessTreeNode>();
  const roots: ProcessTreeNode[] = [];

  // Create nodes for all processes
  for (const ev of processEvidence) {
    if (ev.processId) {
      processMap.set(ev.processId, { evidence: ev, children: [] });
    }
  }

  // Build tree structure
  for (const ev of processEvidence) {
    if (!ev.processId) continue;
    
    const node = processMap.get(ev.processId);
    if (!node) continue;

    if (ev.parentProcessId && processMap.has(ev.parentProcessId)) {
      const parent = processMap.get(ev.parentProcessId);
      parent?.children.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots;
}
